export interface LivrableTangible {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  content: {
    type: 'document' | 'spider' | 'dashboard' | 'framework' | 'topology' | 'presentation';
    data?: any;
    template?: string;
  };
}

export const livrablesTangibles: LivrableTangible[] = [
  {
    id: 'plan-directeur',
    title: 'Plan Directeur TI - Standard ITIL',
    description: 'Présentation interactive ITIL 3-5 ans avec roadmap et KPIs',
    category: 'Stratégie',
    icon: 'Target',
    color: '#0066FF',
    content: {
      type: 'presentation',
      template: `
# PLAN DIRECTEUR TI 2024-2029 - STANDARD ITIL
## Client: [Nom de l'entreprise] | Version: 2.1 | Date: Janvier 2024

---

## 📋 TABLE DES MATIÈRES
1. Contexte et Vision Stratégique
2. Analyse de l'Environnement Actuel (ASIS)
3. Architecture Cible (TOBE)
4. Roadmap ITIL et Transformation
5. Gouvernance et Processus ITIL
6. Investissements et ROI
7. Risques et Mitigation
8. Suivi et Mesure de Performance

---

## 1. 🎯 CONTEXTE ET VISION STRATÉGIQUE

### Mission TI
"Fournir des services TI de classe mondiale qui soutiennent la croissance de 25% du CA en 5 ans, en optimisant l'efficacité opérationnelle et en garantissant la sécurité des données."

### Objectifs Stratégiques ITIL
- **Service Strategy**: Alignement TI-Business
- **Service Design**: Architecture orientée services
- **Service Transition**: Gestion du changement
- **Service Operation**: Excellence opérationnelle
- **Continual Service Improvement**: Amélioration continue

### Valeurs Clés
- **Agilité**: Réponse rapide aux besoins business
- **Fiabilité**: 99.9% de disponibilité
- **Sécurité**: Conformité NIST et ISO 27001
- **Innovation**: Adoption proactive des technologies

---

## 2. 📊 ANALYSE DE L'ENVIRONNEMENT ACTUEL (ASIS)

### Maturité ITIL Actuelle
| Processus ITIL | Niveau Actuel | Niveau Cible | Gap |
|----------------|---------------|--------------|-----|
| Service Desk | 2/5 | 4/5 | -2 |
| Incident Management | 2/5 | 4/5 | -2 |
| Problem Management | 1/5 | 4/5 | -3 |
| Change Management | 2/5 | 4/5 | -2 |
| Configuration Management | 1/5 | 4/5 | -3 |
| Release Management | 1/5 | 3/5 | -2 |
| Service Level Management | 2/5 | 4/5 | -2 |
| Capacity Management | 1/5 | 3/5 | -2 |
| Availability Management | 2/5 | 4/5 | -2 |
| IT Service Continuity | 1/5 | 4/5 | -3 |

### Infrastructure Actuelle
- **Serveurs**: 85% physiques, 15% virtualisés
- **Stockage**: 60% local, 40% SAN
- **Réseau**: Architecture legacy, bande passante limitée
- **Sécurité**: Firewall basique, pas de segmentation
- **Monitoring**: Outils disparates, pas de vue unifiée

### Défis Identifiés
- Absence de processus ITIL formalisés
- Manque de visibilité sur les services
- Temps de résolution incidents élevé
- Gestion des changements non contrôlée
- Absence de mesure de performance

---

## 3. 🏗️ ARCHITECTURE CIBLE (TOBE)

### Vision ITIL 2029
- **Service Strategy**: Portfolio de services aligné business
- **Service Design**: Architecture cloud-native
- **Service Transition**: Pipeline DevOps automatisé
- **Service Operation**: Centre d'opérations unifié
- **CSI**: Amélioration continue basée données

### Infrastructure Cible
- **Cloud-First**: 80% services cloud (AWS/Azure)
- **Hybride**: 20% on-premise pour données critiques
- **Conteneurisation**: Kubernetes pour applications
- **API-First**: Architecture microservices
- **Zero Trust**: Sécurité par design

### Processus ITIL Cible
- **Service Desk**: Outil unifié avec IA
- **ITSM**: Plateforme intégrée (ServiceNow)
- **Monitoring**: Observabilité complète
- **Automation**: 70% des tâches automatisées
- **Governance**: Comité TI mensuel

---

## 4. 🛣️ ROADMAP ITIL ET TRANSFORMATION

### PHASE 1: FONDATIONS (Années 1-2)
**Q1-Q4 2024: Stabilisation**
- Implémentation Service Desk ITIL
- Processus Incident/Problem Management
- Configuration Management Database (CMDB)
- Monitoring de base

**Q1-Q4 2025: Standardisation**
- Change Management ITIL
- Service Level Management
- Release Management
- Formation équipes ITIL

### PHASE 2: OPTIMISATION (Années 3-4)
**Q1-Q4 2026: Automatisation**
- ITSM Platform (ServiceNow)
- Automation des processus
- DevOps Pipeline
- Cloud Migration (50%)

**Q1-Q4 2027: Innovation**
- IA pour Service Desk
- Analytics avancés
- Cloud Native (80%)
- API Management

### PHASE 3: EXCELLENCE (Année 5)
**Q1-Q4 2028: Maturité**
- Continual Service Improvement
- Innovation continue
- Optimisation coûts
- Excellence opérationnelle

---

## 5. 🏛️ GOUVERNANCE ET PROCESSUS ITIL

### Structure de Gouvernance
- **Comité Stratégique TI**: Trimestriel (CEO, CTO, CFO)
- **Comité Opérationnel**: Mensuel (IT Management)
- **Comité de Projet**: Hebdomadaire (Chefs de projet)
- **Comité de Sécurité**: Bimensuel (CISO, IT Security)

### Processus ITIL Implémentés
1. **Service Strategy**
   - Portfolio Management
   - Financial Management
   - Demand Management

2. **Service Design**
   - Service Catalog Management
   - Service Level Management
   - Capacity Management
   - Availability Management

3. **Service Transition**
   - Change Management
   - Release Management
   - Knowledge Management

4. **Service Operation**
   - Incident Management
   - Problem Management
   - Event Management
   - Access Management

5. **Continual Service Improvement**
   - Service Measurement
   - Service Reporting
   - Service Improvement

---

## 6. 💰 INVESTISSEMENTS ET ROI

### Budget 5 Ans (2024-2028)
| Catégorie | Année 1 | Année 2 | Année 3 | Année 4 | Année 5 | Total |
|-----------|---------|---------|---------|---------|---------|-------|
| Infrastructure | 800K$ | 600K$ | 400K$ | 300K$ | 200K$ | 2.3M$ |
| Outils ITIL | 200K$ | 150K$ | 100K$ | 50K$ | 50K$ | 550K$ |
| Formation | 100K$ | 80K$ | 60K$ | 40K$ | 40K$ | 320K$ |
| Personnel | 500K$ | 520K$ | 540K$ | 560K$ | 580K$ | 2.7M$ |
| **TOTAL** | **1.6M$** | **1.35M$** | **1.1M$** | **950K$** | **870K$** | **5.87M$** |

### ROI Attendu
- **Économies opérationnelles**: 2.8M$ (réduction coûts 30%)
- **Gains de productivité**: 1.5M$ (efficacité +25%)
- **Réduction incidents**: 800K$ (temps perdu -60%)
- **ROI Total**: 5.1M$ (87% retour sur investissement)

---

## 7. ⚠️ RISQUES ET MITIGATION

### Risques Techniques
| Risque | Probabilité | Impact | Mitigation |
|--------|-------------|--------|------------|
| Résistance au changement | Élevée | Moyen | Formation, communication |
| Complexité migration | Moyenne | Élevé | Approche progressive |
| Dépendance fournisseurs | Faible | Élevé | Multi-sourcing |
| Budget dépassé | Moyenne | Moyen | Contrôle strict, révisions |

### Risques Business
- **Disruption service**: Plan de continuité
- **Perte de données**: Sauvegarde multi-niveaux
- **Conformité**: Audit régulier
- **Sécurité**: Tests de pénétration

---

## 8. 📈 SUIVI ET MESURE DE PERFORMANCE

### KPIs ITIL
- **Disponibilité**: 99.9% (cible)
- **Temps de résolution**: <2h (incidents P1)
- **Satisfaction client**: >90%
- **Taux de succès changements**: >95%
- **Coût par utilisateur**: -20% en 5 ans

### Tableaux de Bord
- **Service Desk**: Métriques temps réel
- **Infrastructure**: Monitoring 24/7
- **Business**: KPIs alignés objectifs
- **Sécurité**: Dashboard conformité

### Révisions
- **Mensuelle**: Comité opérationnel
- **Trimestrielle**: Comité stratégique
- **Annuelle**: Révision complète plan
- **Ad-hoc**: Ajustements selon contexte

---

## 📋 ANNEXES

### A. Glossaire ITIL
### B. Matrice RACI Processus
### C. Templates de Processus
### D. Plan de Formation
### E. Architecture Technique Détaillée

---

**Document approuvé par:**
- CEO: _________________ Date: _______
- CTO: _________________ Date: _______
- CFO: _________________ Date: _______

**Prochaine révision:** Janvier 2025
      `
    }
  },
  {
    id: 'cadre-nist',
    title: 'Cadre NIST Cybersecurity',
    description: 'Présentation interactive avec spiders imbriqués et détails par fonction',
    category: 'Sécurité',
    icon: 'Shield',
    color: '#E74C3C',
    content: {
      type: 'presentation',
      data: {
        title: "Cadre NIST Cybersecurity Framework 2.0",
        description: "Présentation interactive avec spiders imbriqués et détails par fonction"
      }
    }
  },
  {
    id: 'ia-ethique',
    title: 'IA Éthique et Responsable',
    description: 'Présentation interactive avec spiders et approche BZ pour l\'IA responsable',
    category: 'Innovation',
    icon: 'Brain',
    color: '#9B59B6',
    content: {
      type: 'presentation',
      data: {
        title: "IA Éthique et Responsable - L'approche BZ",
        description: "Présentation interactive avec spiders et approche BZ pour l'IA responsable"
      }
    }
  },
  {
    id: 'dashboard-client',
    title: 'Dashboard Client Complet',
    description: 'Vue d\'ensemble 360° avec profil psychométrique et analyse holistique',
    category: 'Performance',
    icon: 'BarChart3',
    color: '#00B894',
    content: {
      type: 'presentation',
      data: {
        title: "Dashboard Client Complet - Vue 360°",
        description: "Analyse holistique avec profil psychométrique business"
      }
    }
  },
  {
    id: 'catalogue-actifs-ti',
    title: 'Catalogue des Actifs TI',
    description: 'Inventaire complet, topologie réseau, utilisateurs et plan de backup',
    category: 'Infrastructure',
    icon: 'Network',
    color: '#F39C12',
    content: {
      type: 'presentation',
      data: {
        title: "Catalogue des Actifs TI - Vue Holistique",
        description: "Inventaire complet avec topologie, utilisateurs et backup"
      }
    }
  },
  {
    id: 'veille-technologique-ai',
    title: 'Veille Technologique IA',
    description: 'Révolution IA 2024 - Analyse complète et recommandations stratégiques',
    category: 'Innovation',
    icon: 'Brain',
    color: '#6C5CE7',
    content: {
      type: 'presentation',
      data: {
        title: "Veille Technologique IA - Révolution 2024",
        description: "Analyse complète des tendances IA et recommandations stratégiques"
      }
    }
  },
  {
    id: 'budget-ti-optimise',
    title: 'Budget TI Optimisé',
    description: 'Analyse financière complète avec scénarios et recommandations IA',
    category: 'Finance',
    icon: 'DollarSign',
    color: '#2ECC71',
    content: {
      type: 'presentation',
      data: {
        title: "Budget TI Optimisé - Analyse Financière",
        description: "Scénarios budgétaires avec recommandations IA et ROI"
      }
    }
  },
  {
    id: 'comite-ti',
    title: 'Cédule Comité TI',
    description: 'Planification et structure des comités de gouvernance',
    category: 'Gouvernance',
    icon: 'Calendar',
    color: '#34495E',
    content: {
      type: 'presentation',
      data: {
        title: "Comité TI - Gouvernance et Planning",
        description: "Cédule, décisions, roadmap et cadre de gouvernance"
      }
    }
  },
  {
    id: 'charge-projet-outils',
    title: 'Charge de Projet avec Outils',
    description: 'Gestion de projet professionnelle avec outils BZ et méthodologies',
    category: 'Gestion',
    icon: 'FolderKanban',
    color: '#E67E22',
    content: {
      type: 'presentation',
      data: {
        title: "Charge de Projet TactiCe - Machine à Saucisse BZ",
        description: "Interface vendeuse avec AI, KPIs et communication 'Fix the Customer'"
      }
    }
  }
];
